// src/components/Footer/Footer.tsx (Updated version with proper links)
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Instagram, 
  Mail, 
  Phone, 
  MapPin,
  Heart,
  Handshake,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { config } from '../../config';

// Newsletter subscription service
const newsletterService = {
  subscribe: async (email: string) => {
    const response = await fetch(`${config.apiBaseUrl}/email-notifications/newsletter/subscribe/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        source: 'footer'
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to subscribe');
    }

    return response.json();
  }
};

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error' | null;
    text: string;
  }>({ type: null, text: '' });

  // Social links - Only include links to actual StrymHub profiles
  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/strymhub', label: 'Instagram' },
  ];

  // Quick links - only show active pages to avoid low-quality signals
  const quickLinks = [
    { name: 'About Us', href: '/about', active: true },
    { name: 'Contact', href: '/contact', active: true },
    { name: 'FAQ', href: '/faq', active: true },
  ];

  // The studio side of StrymHub
  const studioLinks = [
    { name: 'StrymHub Studios', href: '/studios' },
    { name: 'Productions', href: '/productions' },
    { name: 'Partner With Us', href: '/partners' },
  ];

  // The community side
  const communityLinks = [
    { name: 'The Community', href: '/community' },
    { name: 'The Troupe', href: '/community/troupe' },
    { name: 'Open Calls', href: '/casting' },
    { name: 'Join the Troupe', href: '/community/join' },
  ];

  const categories = [
    { name: 'Originals', href: '/originals' },
    { name: 'Cinema', href: '/media/cinema' },
    { name: 'Contents', href: '/media/contents' },
    { name: 'Podcasts', href: '/podcasts' },
    { name: 'Animations', href: '/animations' },
    { name: 'Sneak Peeks', href: '/sneak-peeks' },
    { name: 'Live', href: '/live' },
  ];

  // Legal links - only show active pages
  const legal = [
    { name: 'Privacy Policy', href: '/privacy-policy' },
    { name: 'Terms of Service', href: '/terms-of-service' },
    { name: 'Cookie Policy', href: '/cookie-policy' },
  ];

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Please enter your email address' });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    setIsLoading(true);
    setMessage({ type: null, text: '' });

    try {
      const result = await newsletterService.subscribe(email);
      setMessage({ 
        type: 'success', 
        text: result.message || 'Successfully subscribed! Please check your email to confirm.' 
      });
      setEmail(''); // Clear the input
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Failed to subscribe. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Clear message after 5 seconds
  React.useEffect(() => {
    if (message.type) {
      const timer = setTimeout(() => {
        setMessage({ type: null, text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <footer className="bg-gradient-to-r from-slate-950 via-neutral-900 to-slate-950 text-white">
      {/* Newsletter Section */}
      <div className="border-b border-orange-500/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="max-w-md mx-auto text-center">
            <h3 className="text-lg font-semibold mb-2 text-white font-display">From the studio floor</h3>
            <p className="text-gray-300 text-sm mb-4">
              New productions, open calls and behind-the-scenes &mdash; straight to your inbox.
            </p>
            
            <form onSubmit={handleNewsletterSubmit} className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-2 bg-slate-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-6 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white font-medium rounded-lg hover:from-orange-700 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Subscribe'
                  )}
                </button>
              </div>
              
              {/* Message Display */}
              {message.type && (
                <div className={`flex items-center justify-center space-x-2 text-sm ${
                  message.type === 'success' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {message.type === 'success' ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <AlertCircle className="h-4 w-4" />
                  )}
                  <span>{message.text}</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <Link to="/" className="logo-text text-3xl font-display bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent mb-4 inline-block text-glow-hover">
              strymhub
            </Link>
            <p className="text-gray-300 mb-6 text-sm leading-relaxed">
              An independent production company in Sheffield, UK. We write, cast and film original
              work with a community-scouted repertory &mdash; and stream all of it here.
            </p>
            
            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center hover:from-orange-600 hover:to-red-600 transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-orange-500/30 group"
                  aria-label={social.label}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <social.icon className="h-5 w-5 text-white group-hover:animate-pulse" />
                </a>
              ))}
            </div>
          </div>

          {/* Studio */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">Studio</h3>
            <ul className="space-y-2">
              {studioLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-white transition-colors duration-200 text-sm hover:translate-x-1 inline-block"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Community */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">Community</h3>
            <ul className="space-y-2">
              {communityLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-white transition-colors duration-200 text-sm hover:translate-x-1 inline-block"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Watch */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">Watch</h3>
            <ul className="space-y-2">
              {categories.map((category) => (
                <li key={category.name}>
                  <Link
                    to={category.href}
                    className="text-gray-300 hover:text-white transition-colors duration-200 text-sm hover:translate-x-1 inline-block"
                  >
                    {category.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Legal */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">Get in Touch</h3>
            
            {/* Contact Info */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center text-gray-300 text-sm">
                <Mail className="h-4 w-4 mr-2 text-orange-400" />
                <a 
                  href="mailto:contact@strymhub.com" 
                  className="hover:text-white transition-colors"
                >
                  contact@strymhub.com
                </a>
              </div>
              <div className="flex items-center text-gray-300 text-sm">
                <Handshake className="h-4 w-4 mr-2 text-orange-400" />
                <a
                  href="mailto:partnerships@strymhub.com"
                  className="hover:text-white transition-colors"
                >
                  partnerships@strymhub.com
                </a>
              </div>
              <div className="flex items-center text-gray-300 text-sm">
                <Phone className="h-4 w-4 mr-2 text-orange-400" />
                <a 
                  href="tel:+447497088515" 
                  className="hover:text-white transition-colors"
                >
                  +44 74 9708 8515
                </a>
              </div>
              <div className="flex items-center text-gray-300 text-sm">
                <MapPin className="h-4 w-4 mr-2 text-orange-400" />
                Sheffield, UK
              </div>
            </div>

            {/* Studio links */}
            <div className="mb-6">
              <h4 className="text-sm font-medium mb-2 text-gray-200">More</h4>
              <ul className="space-y-1">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-gray-400 hover:text-gray-200 transition-colors duration-200 text-xs"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h4 className="text-sm font-medium mb-2 text-gray-200">Legal</h4>
              <ul className="space-y-1">
                {legal.slice(0, 3).map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className="text-gray-400 hover:text-gray-300 transition-colors duration-200 text-xs"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex items-center text-sm text-gray-400">
              <span>© 2025 StrymHub. Made with</span>
              <Heart className="h-4 w-4 mx-1 text-red-500 animate-pulse" />
              <span>in Sheffield</span>
            </div>
            
            <div className="flex flex-wrap items-center space-x-6 text-xs text-gray-400">
              <span>All rights reserved</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;